import {
  AdminDatePickerInputModal,
  AdminInput,
  AdminRadioInput,
  Button,
  dateTimeInitial,
  Div,
  ErrorToast,
  H5,
  Modal,
  PropsModal,
  Separator,
} from '@vgrubs/components';
import { WeekDay } from 'components/src/types/location.types';
import {
  daysOfWeekStringV2,
  MenuAvailabilityHourV2Response,
  MenuAvailabilityV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import { DateTime } from 'luxon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { formatDateToTime24h } from '../../../../helpers/time-helpers';
import { isMenuProviderDelivery } from '../../Menu';

export const AddEditAvailability = ({
  modalIsOpen,
  closeModal,
  setMenu,
  edit,
}: PropsModal & {
  modalIsOpen: boolean | MenuAvailabilityV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  edit?: boolean;
}) => {
  const [name, setName] = useState('');
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<WeekDay[]>([]);

  const [timeFrom, setTimeFrom] = useState<Date[]>([dateTimeInitial]);
  const [timeTo, setTimeTo] = useState<Date[]>([dateTimeInitial]);

  const { provider } = useParams() as {
    provider: Provider;
    locationId: string;
    brandId: string;
  };

  const data = modalIsOpen;

  const emptyForm = () => {
    setName('');
    setSelectedDayOfWeek([]);
    setTimeFrom([dateTimeInitial]);
    setTimeTo([dateTimeInitial]);
  };

  useEffect(() => {
    if (edit && data && typeof data === 'object') {
      setName(data.name);
    } else {
      emptyForm();
    }
  }, [data]);

  const isProviderDelivery = isMenuProviderDelivery(provider);

  async function handleAdd() {
    if (!name || selectedDayOfWeek.length < 1 || !timeFrom || !timeTo)
      return ErrorToast('Please fill in all information');

    // Parse the time values with Luxon
    const timeFromDate = DateTime.fromFormat(
      formatDateToTime24h(timeFrom[0]),
      'HH:mm'
    );
    const timeToDate = DateTime.fromFormat(
      formatDateToTime24h(timeTo[0]),
      'HH:mm'
    );

    // Ensure "Time From" is earlier than "Time To"
    if (timeFromDate >= timeToDate)
      return ErrorToast('Time From must be earlier than Time To');

    // Ensure at least 1 hour difference between "From" and "To"
    const timeDifference = timeToDate.diff(timeFromDate, 'hours').hours;
    if (timeDifference < 1)
      return ErrorToast(
        'There must be at least 1 hour between Time From and Time To'
      );

    const hrs = [] as MenuAvailabilityHourV2Response[];

    if (edit && typeof data === 'object') {
      data?.hours.forEach((element) => {
        return hrs.push({
          day: element.day,
          startTime: element.startTime,
          endTime: element.endTime,
          id: null,
        });
      });
    }

    const bodyEdit = {
      name,
      hours: hrs,
    };

    const uuid = Math.floor(Math.random() * 1000000);

    const bodyAdd: MenuAvailabilityV2Response = {
      id: uuid,
      name,
      hours: selectedDayOfWeek.map((t) => {
        return {
          id: null,
          day: t as WeekDay,
          startTime: formatDateToTime24h(timeFrom[0]),
          endTime: formatDateToTime24h(timeTo[0]),
        };
      }),
    };

    // If editing, update the existing modifier group in the menu state
    if (edit && typeof data === 'object') {
      setMenu((prev) => {
        if (typeof prev === 'boolean' || !prev) return prev;
        const updated = prev.availability.map((availability) => {
          if (availability.id === data?.id) {
            return { ...availability, ...bodyEdit };
          } else return availability;
        });

        return { ...prev, availability: updated };
      });
    } else {
      // If adding a new modifier group, add it to the appropriate category
      setMenu((prev) => {
        if (typeof prev === 'boolean' || !prev) return prev;

        const updated = [...prev.availability, bodyAdd];

        return { ...prev, availability: updated };
      });
    }
    emptyForm();
    closeModal?.();
  }

  const toggleDaySelection = (day: WeekDay) => {
    setSelectedDayOfWeek(
      (prevSelected) =>
        prevSelected.includes(day)
          ? prevSelected.filter((d) => d !== day) // Remove if already selected
          : [...prevSelected, day] // Add if not selected
    );
  };

  return (
    <Modal
      isOpen={!!modalIsOpen}
      closeModal={closeModal}
      logo={false}
      header={`${edit ? 'Edit' : 'Add'} Availability`}
      textAlign="left"
      width="500px"
    >
      {isProviderDelivery && (
        <Div $mb="32">
          <AdminInput
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
            label="Name"
            placeholder="Enter here"
            $textCenter
          />
        </Div>
      )}

      {!edit && (
        <>
          <H5 $center>Select Day</H5>
          <Separator />
          {daysOfWeekStringV2.map(({ name, value }) => (
            <div key={value}>
              <AdminRadioInput
                label={name}
                checked={selectedDayOfWeek.includes(value)}
                onClick={() => toggleDaySelection(value)}
              />
              <Separator />
            </div>
          ))}
          <H5 $mt="32" $mb="16" $center>
            Select Hours From - To
          </H5>
          <Div $flex $gap="24px">
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="From"
              $textCenter
              onChange={setTimeFrom}
              value={timeFrom}
            />
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="To"
              $textCenter
              onChange={setTimeTo}
              value={timeTo}
            />
          </Div>
        </>
      )}

      <Button $selected $w="100%" $mt="32" onClick={() => handleAdd()}>
        Confirm
      </Button>
      <Button $selected $gray $w="100%" $mt="16" onClick={() => closeModal?.()}>
        Cancel
      </Button>
    </Modal>
  );
};
